'use es6';

import { AutoSizer, InfiniteLoader, List as VirtualizedList } from 'react-virtualized';
import { BASE_FONT_SIZE } from 'HubStyleTokens/sizes';
import { BUTTON_DISABLED_TEXT } from 'HubStyleTokens/colors';
import { ENTER } from 'UIComponents/constants/KeyCodes';
import { isFieldRestricted } from '../../permissions/fieldLevelPermissions';
import { Map as ImmutableMap } from 'immutable';
import { WEB_FONT_FAMILY, WEB_FONT_REGULAR_WEIGHT } from 'HubStyleTokens/misc';
import { makeFuzzyRegExp } from 'customer-data-objects/search/FieldSearch';
import { mapOf, setOf } from 'react-immutable-proptypes';
import FilterFieldGroupType from 'customer-data-filters/components/propTypes/FilterFieldGroupType';
import FilterFieldType from 'customer-data-filters/components/propTypes/FilterFieldType';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ListPlaceholderSvg from 'customer-data-ui-utilities/images/ListPlaceholderSvg';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import RestrictedPropertyIcon from './RestrictedPropertyIcon';
import ShimmerWrapper from 'customer-data-ui-utilities/images/ShimmerWrapper';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import UIList from 'UIComponents/list/UIList';
import UISearchInput from 'UIComponents/input/UISearchInput';
import UISelect from 'UIComponents/input/UISelect';
import UITruncateString from 'UIComponents/text/UITruncateString';
import UITypeahead from 'UIComponents/typeahead/UITypeahead';
import always from 'transmute/always';
import has from 'transmute/has';
import isString from 'transmute/isString';
import memoize from 'transmute/memoize';
import memoizeLast from 'transmute/memoizeLast';
import partial from 'transmute/partial';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GROUP_NAME_HEIGHT = 40;
const VALUE_HEIGHT = 33;
const styles = {
  /**
   * For this component, all styles for this that can live in JS should live in
   * JS to keep runtime text measurements consistent.
   */
  result: {
    display: 'block',
    fontFamily: WEB_FONT_FAMILY,
    fontSize: BASE_FONT_SIZE,
    fontWeight: WEB_FONT_REGULAR_WEIGHT,
    lineHeight: '1.25em',
    padding: `8px 4px`,
    margin: '0px',
    whiteSpace: 'normal',
    width: '100%',
    wordBreak: 'normal'
  },
  ruler: {
    clip: 'rect(0, 0, 0, 0)',
    position: 'fixed',
    visibility: 'hidden',
    zIndex: '-100000'
  }
};
function getSortedFieldGroups(getLabelString, fieldGroups) {
  return fieldGroups.valueSeq().sortBy(({
    displayOrder
  }) => displayOrder).map(group => group.update('properties', properties => properties.sortBy(getLabelString))).toList();
}
function getSortedFields(getLabelString, fields) {
  return fields.valueSeq().sortBy(getLabelString).sortBy(({
    displayOrder
  }) => displayOrder).toList();
}
export const searchLookup = (getLabelString, fields, search) => {
  const searchQueryWithNoDiacritics = isString(search) ? UISelect.stripDiacritics(search) : search;
  const pattern = makeFuzzyRegExp(searchQueryWithNoDiacritics);
  const result = [];
  const fieldsLength = fields.size;
  for (let index = 0; index < fieldsLength; index++) {
    const field = fields.get(index);
    const labelString = getLabelString(field);
    const labelStringWithNoDiacritics = isString(labelString) ? UISelect.stripDiacritics(labelString) : labelString;
    if (pattern.test(labelStringWithNoDiacritics)) {
      result.push(field);
    }
  }
  return result;
};
const _isHeadingEntry = entry => has('heading', entry);
const _isSubheadingEntry = entry => has('properties', entry);
const _isSelectableEntry = entry => entry && !_isHeadingEntry(entry) && !_isSubheadingEntry(entry);
function lookupGroups(getLabelString, fieldGroups, search) {
  const result = [];
  const groupsLength = fieldGroups.size;
  let lastGroupWasMostUsedProps = false;
  for (let i = 0; i < groupsLength; i++) {
    const group = fieldGroups.get(i);
    let groupLength;
    if (group.properties.size === 0) {
      continue;
    }
    if (lastGroupWasMostUsedProps) {
      result.push(ImmutableMap({
        heading: 'allProperties'
      }));
      lastGroupWasMostUsedProps = false;
    }
    if (group.name === 'most_used_properties') {
      groupLength = result.push(ImmutableMap({
        heading: 'mostUsedProperties'
      }));
      lastGroupWasMostUsedProps = true;
    } else {
      groupLength = result.push(group);
    }
    const properties = group.properties;
    result.push(...searchLookup(getLabelString, properties, search));
    if (result.length === groupLength) {
      result.pop();
    }
  }
  const isResultEmpty = !result.some(_isSelectableEntry);
  return isResultEmpty ? [] : result;
}
export default class FilterEditorFieldSelect extends PureComponent {
  constructor() {
    super();
    this.handleFieldChange = field => {
      const {
        getOperators,
        onChange
      } = this.props;
      const Operator = getOperators(field).first();
      const value = Operator.of(field);
      onChange(syntheticEvent(value));
    };
    this.handleLoadMoreRows = ({
      startIndex
    }) => {
      const {
        filterFamily,
        loadMoreFields,
        searchValue
      } = this.props;
      loadMoreFields(filterFamily, startIndex, searchValue);
    };
    this.handleScroll = () => {
      const {
        scrollTop
      } = this.state;
      if (scrollTop !== undefined) {
        this.setState({
          scrollTop: undefined
        });
      }
    };
    this.handleSearchChange = evt => {
      const {
        filterFamily,
        loadMoreFields,
        onSearchChange
      } = this.props;
      loadMoreFields(filterFamily, 0, evt.target.value);
      this.setState({
        scrollTop: 0
      });
      onSearchChange(evt);
    };
    this.handleSearchKeyDown = evt => {
      const {
        keyCode
      } = evt;
      const {
        matches = []
      } = this.state;
      const firstEntry = matches.find(_isSelectableEntry);
      if (keyCode === ENTER && firstEntry) {
        evt.stopPropagation();
        this.handleFieldChange(firstEntry);
      }
    };
    this.state = {
      matches: []
    };
    this.getRowHeight = this.getRowHeight.bind(this);
    this.getSortedFieldGroups = memoizeLast(getSortedFieldGroups);
    this.getSortedFields = memoizeLast(getSortedFields);
    // In new code, please use ES6 spread args rather than partials
    // for easier debugging and readability:
    // (...args) => myFunction('partiallyAppliedParam', ...args)
    this.partial = memoize(partial);
    this.renderList = this.renderList.bind(this);
    this.renderListPlaceholder = this.renderListPlaceholder.bind(this);
    this.renderRow = this.renderRow.bind(this);
  }
  componentDidMount() {
    const {
      filterFamily,
      loadMoreFields,
      searchValue
    } = this.props;
    loadMoreFields(filterFamily, 0, searchValue);
    this.updateMatches(this.props);
  }
  componentDidUpdate(lastProps) {
    const didChange = propName => typeof this.props[propName] !== 'undefined' && lastProps[propName] !== this.props[propName];
    if (didChange('searchValue') || didChange('fieldGroups') || didChange('fields')) {
      this.updateMatches(this.props);
    }
  }
  getRowHeight(row) {
    const entry = this.state.matches[row.index];
    return entry.properties ? GROUP_NAME_HEIGHT : VALUE_HEIGHT;
  }
  updateMatches(props) {
    const {
      fieldGroups,
      fields,
      getLabelString,
      isXoEnabled,
      searchValue
    } = props;
    let matches;
    if ((!fieldGroups || fieldGroups.size === 0) && isXoEnabled) {
      const sortedFields = this.getSortedFields(getLabelString, fields || ImmutableMap());
      matches = searchLookup(getLabelString, sortedFields, searchValue);
    } else {
      const sortedGroups = this.getSortedFieldGroups(getLabelString, fieldGroups || ImmutableMap());
      matches = lookupGroups(getLabelString, sortedGroups, searchValue);
    }
    this.setState({
      matches
    });
  }
  renderListPlaceholder(dimensions) {
    const paddingTop = 12;
    return /*#__PURE__*/_jsx("div", {
      style: {
        height: dimensions.height,
        paddingTop: `${paddingTop}px`,
        width: dimensions.width
      },
      children: /*#__PURE__*/_jsx(UIList, {
        children: /*#__PURE__*/_jsx(ShimmerWrapper, {
          children: /*#__PURE__*/_jsx(ListPlaceholderSvg, {
            height: dimensions.height - paddingTop,
            rowHeight: 14,
            rowMinWidth: 120,
            rowPaddingX: 16,
            width: dimensions.width
          })
        })
      })
    });
  }
  renderList(dimensions) {
    const {
      matches = [],
      scrollTop
    } = this.state;
    const isRowLoaded = ({
      index
    }) => index < matches.length;
    const rowCount = matches.length + 1;
    return /*#__PURE__*/_jsx(InfiniteLoader, {
      isRowLoaded: isRowLoaded,
      loadMoreRows: this.handleLoadMoreRows,
      rowCount: rowCount,
      children: ({
        onRowsRendered,
        registerChild
      }) => /*#__PURE__*/_jsx(VirtualizedList, {
        height: dimensions.height,
        noRowsRenderer: this.renderNoRows,
        onRowsRendered: onRowsRendered,
        onScroll: this.handleScroll,
        ref: registerChild,
        rowCount: matches.length,
        rowHeight: this.getRowHeight,
        rowRenderer: this.renderRow,
        scrollTop: scrollTop,
        width: dimensions.width
      })
    });
  }
  renderNoRows() {
    return /*#__PURE__*/_jsx("span", {
      className: "p-top-3",
      style: Object.assign({}, styles.result, {
        color: BUTTON_DISABLED_TEXT
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: UITypeahead.noMatchesI18nKey
      })
    });
  }
  renderRow(row) {
    const {
      isXoEnabled,
      fieldGroups
    } = this.props;
    const entry = this.state.matches[row.index] || ImmutableMap();
    if (entry.get('heading')) {
      return this.renderHeadingRow(row, entry);
    }
    if (entry.properties && (!isXoEnabled || fieldGroups && fieldGroups.size > 0)) {
      return this.renderSubheadingRow(row, entry);
    }
    return this.renderPropertyRow(row, entry);
  }
  renderHeadingRow(row, entry) {
    const message = `filterSidebar.${entry.get('heading')}`;
    return /*#__PURE__*/_jsx("h5", {
      className: "m-all-0 p-x-1 p-y-3",
      style: row.style,
      children: /*#__PURE__*/_jsx(UITruncateString, {
        placement: "right",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: message
        })
      })
    }, row.key);
  }
  renderSubheadingRow(row, entry) {
    return /*#__PURE__*/_jsx("div", {
      className: "is--heading-7 m-all-0 p-x-1 p-y-3",
      style: row.style,
      children: /*#__PURE__*/_jsx(UITruncateString, {
        placement: "right",
        children: entry.get('displayName')
      })
    }, row.key);
  }
  renderPropertyRow(row, entry) {
    const {
      activeFieldNames,
      getLabelString,
      FieldSelectItemComponent
    } = this.props;
    const isActiveFieldName = activeFieldNames.contains(entry.name);
    const isRestricted = isFieldRestricted(entry.accessLevel);
    return /*#__PURE__*/_jsx("div", {
      style: row.style,
      children: /*#__PURE__*/_jsx(FieldSelectItemComponent, {
        className: "result",
        "data-selenium-info": `${entry.name}`,
        "data-selenium-test": "filter-result-item",
        disabled: isActiveFieldName || isRestricted || entry.disabled,
        field: entry,
        onClick: this.partial(this.handleFieldChange, entry),
        style: styles.result,
        children: /*#__PURE__*/_jsx(UITruncateString, {
          fixedChildren: isRestricted ? /*#__PURE__*/_jsx(RestrictedPropertyIcon, {}) : undefined,
          placement: "right",
          useFlex: true,
          children: getLabelString(entry)
        })
      })
    }, row.key);
  }
  render() {
    const {
      fieldGroups,
      fields,
      isXoEnabled,
      placeholder,
      searchValue,
      searchable
    } = this.props;
    const baseRowHeight = 30;
    const arbitraryMinHeight = `${baseRowHeight / 2 + GROUP_NAME_HEIGHT}px`;
    return /*#__PURE__*/_jsxs("div", {
      className: "flex-grow-1 flex-column",
      children: [!isXoEnabled && /*#__PURE__*/_jsx("div", {
        className: "is--heading-7 m-all-0 p-bottom-3",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "filterSidebar.addFilter"
        })
      }), searchable && /*#__PURE__*/_jsx(UISearchInput, {
        autoFocus: true,
        className: "m-bottom-2",
        "data-selenium-test": "filter-search-input",
        icon: isXoEnabled,
        onChange: this.handleSearchChange,
        onKeyDown: this.handleSearchKeyDown,
        placeholder: isXoEnabled ? undefined : placeholder,
        value: searchValue
      }), /*#__PURE__*/_jsx("div", {
        className: "flex-grow-1",
        style: {
          minHeight: arbitraryMinHeight
        },
        children: /*#__PURE__*/_jsx(AutoSizer, {
          children: dimensions => dimensions.height > 0 && fields === undefined && fieldGroups === undefined ? this.renderListPlaceholder(dimensions) : this.renderList(dimensions)
        })
      })]
    });
  }
}

/**
 * Dear future us,
 *
 * In the dev environment, the propTypes for this component can be a big
 * bottle neck if the portal has a lot of properties (e.g. in 53). The
 * good news is those don't run on production.
 *
 * If you're running into some slowness, try commenting out the type for
 * `fieldGroups` before getting too deep into the perf tools.
 *
 * Sincerely,
 * Past Colby
 */
FilterEditorFieldSelect.defaultProps = {
  filterFamily: '',
  loadMoreFields: always(false),
  searchable: true
};
FilterEditorFieldSelect.propTypes = {
  FieldSelectItemComponent: PropTypes.func,
  activeFieldNames: setOf(PropTypes.string.isRequired).isRequired,
  fieldGroups: mapOf(FilterFieldGroupType.isRequired),
  fields: mapOf(FilterFieldType),
  filterFamily: PropTypes.string.isRequired,
  getLabelString: PropTypes.func.isRequired,
  getOperators: PropTypes.func.isRequired,
  isXoEnabled: PropTypes.bool,
  loadMoreFields: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  searchValue: PropTypes.string.isRequired,
  searchable: PropTypes.bool.isRequired
};